import { useLayoutEffect, useState } from 'react';
import { Button } from 'react-bootstrap';
import toast from 'react-hot-toast';
import { formatMessage } from '../../../../../translations/format-message';
import ImageContentView from './image-content-view';
import { useFormikContext } from 'formik';
import { observer } from 'mobx-react-lite';
import TaplerView from './image-content-types/tapler-csv-view';
import DimensionTableView from './image-content-types/dimension-table-view';
import CheckItemsView from './image-content-types/check-items-view';
import BOMView from './image-content-types/bom-data-view';
import LEDTableView from './image-content-types/led-table-view';
import { IProductFiles } from '../../../../../models/response/IProductResponse';
import ProcessTypeEnum from '../../../../../constants/enums/process-types';
import CorrectionRequestModal from '../../correction-request-modal';

import { useStore } from '../../../../../contexts/store-provider';
import { getTenantIdFromLocalStorage } from '../../../../../helpers/localstorage-helper';
import { ExclamationCircleFill } from 'react-bootstrap-icons';

interface IProps {
  id: number;
  processFileDetails: any;
  processType: number;
  selectedProcess: number;
  selectedImage: IProductFiles[];
  selectedImageIndex: number;
}
enum imageDataType {
  tapler = 0,
  check_item = 1,
  dimension_table = 2,
  led = 3,
}

const ImageTabView = observer((props: IProps) => {
  const {
    selectedImage,
    selectedImageIndex,
    processFileDetails,
    processType,
    selectedProcess,
  } = props;
  const { productStore, correctionControlStore } = useStore();
  const {
    GetCorrectionControlService,
    correctionControlState,
    correctionControlData,
    UpdateCorrectionControlService,
    updateCorrectionControlState,
  } = productStore;
  const { GetPendingCountService } = correctionControlStore;

  const [imageDataTypeArray, setImageDataTypeArray] = useState<any[]>([]);
  const [showCorrectionModal, setShowCorrectionModal] = useState<boolean>(false);
  const { values, setFieldValue } = useFormikContext<any>();

  const handleCorrectionButtonClick = () => {
    setShowCorrectionModal(true);
    const tenantId = Number(getTenantIdFromLocalStorage());
    const productId = props.id || values?.Id || values?.ProductId;
    const processId = selectedProcess || processFileDetails?.ProcessId;
    GetCorrectionControlService(tenantId, productId, processId);
  };

  const handleCompletion = async () => {
    const tenantId = Number(getTenantIdFromLocalStorage());
    // The GetCorrectionControl API returns the correction entries under
    // Data.CorrectionList (see correction-request-modal.tsx's own responseData/correctionList
    // derivation) - not a single Data.ID/Data.Id field, which is why CorrectionId was always
    // going out as an empty array before.
    const responseData = correctionControlData?.Data || correctionControlData?.data;
    const correctionListRaw = Array.isArray(responseData?.CorrectionList)
      ? responseData.CorrectionList
      : Array.isArray(responseData)
        ? responseData
        : responseData
          ? [responseData]
          : [];
    const correctionIds = correctionListRaw
      .map((item: any) => item?.ID ?? item?.Id ?? item?.id)
      .filter((id: any) => id !== undefined && id !== null);

    const payload = {
      TenantId: tenantId,
      CorrectionId: correctionIds,
    };

    const res = await UpdateCorrectionControlService(payload);

    if (res && (!res.Error || res.Error === false)) {
      if (tenantId && Number(tenantId) > 0) {
        GetPendingCountService(Number(tenantId));
      }
      // processFileDetails is a Formik-owned object (values.Process.find(...)) - mutating its
      // fields directly doesn't trigger Formik/React to re-render, which is why the "!" mark
      // used to stay visible until an unrelated re-render (e.g. switching tabs) picked up the
      // already-mutated object. Go through setFieldValue with a fresh array/object instead so
      // the mark disappears immediately.
      const processId = selectedProcess || processFileDetails?.ProcessId;
      const updatedProcessList = (values.Process || []).map((p: any) => {
        if (p.ProcessId !== processId) return p;
        const updated = { ...p };
        if (updated.CorrectionRequest && typeof updated.CorrectionRequest === 'object') {
          updated.CorrectionRequest = {
            ...updated.CorrectionRequest,
            IsCompleted: true,
            IsMarked: false,
          };
        } else {
          updated.CorrectionRequest = false;
        }
        updated.IsCorrectionCompleted = true;
        updated.HasCorrectionRequest = false;
        updated.HasCorrectionRequestMark = false;
        return updated;
      });
      setFieldValue('Process', updatedProcessList);
      setShowCorrectionModal(false);
      toast.success(formatMessage(res?.Message || 'complete_success'));
    } else {
      toast.error(formatMessage(res?.Message || 'error_message'));
    }
  };
  const [selectedContent, setSelectedContent] = useState<number>(
    imageDataTypeArray.length > 0 ? imageDataTypeArray[0].enumId : -1
  );
  const [activeTab, setActiveTab] = useState<string>(
    imageDataTypeArray.length > 0 ? imageDataTypeArray[0].enumName : ''
  );

  useLayoutEffect(() => {
    setImageDataTypeArray(() => []);

    if (Number(processType) === ProcessTypeEnum.LedLength) {
      setImageDataTypeArray([
        { enumId: imageDataType.led, enumName: 'LED' },
        { enumId: imageDataType.check_item, enumName: 'check_item' },
      ]);
    } else if (Number(processType) === ProcessTypeEnum.Print) {
      setImageDataTypeArray([
        { enumId: imageDataType.tapler, enumName: 'tapler' },
        { enumId: imageDataType.check_item, enumName: 'check_item' },
      ]);
    } else {
      setImageDataTypeArray([
        { enumId: imageDataType.check_item, enumName: 'check_item' },
      ]);
    }
  }, [processType, selectedProcess, values?.Process]);

  useLayoutEffect(() => {
    if (imageDataTypeArray.length > 0) {
      const activeTabName = imageDataTypeArray.find(
        (item) => item.enumId === selectedContent
      )?.enumName;
      setActiveTab(activeTabName || null);
    }
  }, [imageDataTypeArray, processType, selectedContent]);

  const renderTabView = () => {
    if (activeTab === 'LED' || activeTab === 'led') {
      return (
        <LEDTableView
          id={props.id}
          selectedProcess={selectedProcess}
          selectedImage={selectedImage}
          selectedImageIndex={selectedImageIndex}
        />
      );
    } else if (activeTab === 'tapler') {
      return (
        <TaplerView
          id={props.id}
          selectedImage={selectedImage}
          selectedImageIndex={selectedImageIndex}
        />
      );
    } else if (activeTab === 'dimension_table') {
      return (
        <CheckItemsView
          id={props.id}
          selectedProcess={selectedProcess}
          selectedImage={selectedImage}
          selectedImageIndex={selectedImageIndex}
        />
      );
    } else if (activeTab === 'check_item') {
      return (
        <CheckItemsView
          id={props.id}
          selectedProcess={selectedProcess}
          selectedImage={selectedImage}
          selectedImageIndex={selectedImageIndex}
        />
      );
    } else if (activeTab === 'bom') {
      return <BOMView />;
    }
    return null;
  };

  // imageDataTypeArray is rebuilt (as a brand new array reference) by the effect above every
  // time values.Process changes — which includes editing a check item, since check items live
  // inside values.Process. Only fall back to the first tab when the currently selected tab no
  // longer exists in the rebuilt array; otherwise a check-item edit would keep bouncing the user
  // back to the first tab (e.g. Tapler) instead of staying on Check Item.
  useLayoutEffect(() => {
    if (imageDataTypeArray.length === 0) return;
    const isSelectedContentStillValid = imageDataTypeArray.some(
      (item) => item.enumId === selectedContent
    );
    if (!isSelectedContentStillValid) {
      setSelectedContent(imageDataTypeArray[0].enumId);
      setActiveTab(imageDataTypeArray[0].enumName);
    }
  }, [imageDataTypeArray]);

  return (
    <div className={`check-item-list checkItemsDimension check-item-step-4 `}>
      <div className={`d-flex align-items-center justify-content-between ${activeTab?.toLowerCase() === 'led' ? 'led' : ''} ${Boolean(
          (processFileDetails?.CorrectionRequest && typeof processFileDetails?.CorrectionRequest === 'object' && !processFileDetails?.CorrectionRequest?.IsCompleted) ||
          processFileDetails?.CorrectionRequest === true ||
          (processFileDetails?.HasCorrectionRequest && !processFileDetails?.IsCorrectionCompleted)
        ) && ('hasCorrectionRequest')}`}>
        <ul
          className="nav nav-tabs border-0 mb-0"
          id="imageDataTypeTab"
          role="tablist"
        >
          {imageDataTypeArray.map((item) => (
            <li
              className={`nav-item ${selectedContent === item.enumId ? 'active' : ''}`}
              role="presentation"
              key={item.enumId}
              onClick={() => setSelectedContent(item.enumId)}
            >
              <button
                key={item.enumId}
                className={`nav-link ${selectedContent === item.enumId ? 'active' : ''}`}
                id={`imageDataType-${item.enumId}-tab`}
                type="button"
                role="tab"
                aria-controls={`imageDataType-${item.enumId}`}
                aria-selected={selectedContent === item.enumId ? 'true' : 'false'}
                onClick={() => setSelectedContent(item.enumId)}
              >
                {formatMessage(item.enumName)}
              </button>
            </li>
          ))}
        </ul>

        {Boolean(
          (processFileDetails?.CorrectionRequest && typeof processFileDetails?.CorrectionRequest === 'object' && !processFileDetails?.CorrectionRequest?.IsCompleted) ||
          processFileDetails?.CorrectionRequest === true ||
          (processFileDetails?.HasCorrectionRequest && !processFileDetails?.IsCorrectionCompleted)
        ) && (
          <Button
            variant="danger"
            title={formatMessage('correction_request')}
            className=" me-1"
            onClick={handleCorrectionButtonClick}
          >
            <ExclamationCircleFill />
          </Button>
        )}
      </div>

      <div
        className="tab-content pb-0 border"
        id="imageDataTypeTabContent"
      >
        {renderTabView()}
      </div>

      <CorrectionRequestModal
        show={showCorrectionModal}
        onHide={() => setShowCorrectionModal(false)}
        isLoading={correctionControlState?.inProgress}
        isSubmitting={updateCorrectionControlState?.inProgress}
        apiData={correctionControlData}
        onComplete={handleCompletion}
        productId={values?.Id || values?.ProductName || values?.Katashiki || ''}
        processName={processFileDetails?.ProcessName || ''}
        modificationDetails={
          processFileDetails?.CorrectionRequest?.ModificationDetails ||
          processFileDetails?.CorrectionDetails ||
          processFileDetails?.Remarks ||
          ''
        }
        imagePath={
          processFileDetails?.CorrectionRequest?.ImagePath ||
          processFileDetails?.CorrectionImagePath ||
          processFileDetails?.fileIndices?.[0]?.FileDetail?.[0]?.S3FilePath ||
          processFileDetails?.fileIndices?.[0]?.FileDetail?.[0]?.FilePath ||
          selectedImage?.[0]?.S3FilePath ||
          selectedImage?.[0]?.FilePath ||
          ''
        }
        fileType={processFileDetails?.CorrectionRequest?.FileType || ''}
      />
    </div>
  );
});

export default ImageTabView;
